import {
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  Typography,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import './models-details.scss';
import { useNavigate, useSearchParams } from 'react-router';
import { FavoriteButton } from '../../possibly-stateful-components/FavoriteButton/FavoriteButton.tsx';
import {
  FavoriteProperties,
  HomeModelFavoriteProperties,
} from '../../models/favorites.model.ts';
import { ImageCarousel } from '../../possibly-stateful-components/ImageCarousel/ImageCarousel.tsx';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useGetHomeModelById } from '../../api/homes.ts';
import { cloudinaryBaseUrl } from '../../api/styleBoard.ts';
import { useGetHomeModelFavorites } from '../../api/favorites.ts';
import { useMemo, useState } from 'react';
import { ShareModal } from '../../components/share-modal/share-modal.tsx';
import { useTitle } from '../../hooks/useTitle.ts';
import { useTimedState } from '../../hooks/use-timed-state.ts';

export const ModelsDetailsView = () => {
  const [params] = useSearchParams();

  const id = params.get('id');

  const { loading, data: homeModelData } = useGetHomeModelById(id || '');
  const navigate = useNavigate();

  const { data: homeModelFavorites } = useGetHomeModelFavorites();

  const [showingShareModal, setShowingShareModal] = useState(false);

  const [showFavoriteMessage, setShowFavoriteMessage] = useTimedState(
    false,
    2500
  );

  const handleAddToFavorites = () => {
    setShowFavoriteMessage(true);
  };

  useTitle(
    loading || !homeModelData
      ? 'Loading Model Details - Immersive Homes'
      : `${homeModelData.name || 'Model Details'} - Immersive Homes`
  );

  const priceRangeText = useMemo(() => {
    const rangeArray = [
      Number(homeModelData?.price_min) || 0,
      Number(homeModelData?.price_max) || 0,
    ] as const;

    let text = 'Starting from';

    if (rangeArray[0]) text += ` $${rangeArray[0].toLocaleString() || ''}`;

    if (rangeArray[1]) text += ` - $${rangeArray[1].toLocaleString() || ''}`;

    return text;
  }, [homeModelData]);

  const navigateToPanorama = () => {
    if (homeModelData?.sku) {
      void navigate(`/panorama?homeId=${homeModelData?.sku}&currentSkuIds={}`);
    }
  };

  if (loading || !homeModelData) {
    return (
      <div className="loading">
        <CircularProgress sx={{ marginRight: '12px' }} />
        <Typography>Loading...</Typography>{' '}
      </div>
    );
  } else {
    return (
      <Box className="models-details-page" sx={{ flex: 1, width: '100%' }}>
        <Box className="container">
          <Box className="item titles">
            <div className="results-list-item">
              <FavoriteButton
                id={id || ''}
                favorites={[]}
                result={{ id: id } as FavoriteProperties}
              />
              <FavoriteButton
                id={id ? id.toString() : ''}
                homeModelFavorites={homeModelFavorites}
                addtoFavoritesFunc={handleAddToFavorites}
                saveHomeModelResult={
                  { sk: id ? id.toString() : '' } as HomeModelFavoriteProperties
                }
              />
              <IconButton
                aria-label="share"
                color="primary"
                className="share-button"
                sx={{ borderRadius: '0px' }}
                onClick={() => {
                  setShowingShareModal(true);
                }}
              >
                <div className="button-tooltip">Share</div>
                <IosShareIcon sx={{ color: '#49454F' }} />
              </IconButton>
              <ImageCarousel
                height="320px"
                homeModelsResponseData={homeModelData || undefined}
                key={'details-page'}
                openBigModeOnClick
              />
              <Box className="favorite-alert">
                <Fade
                  in={showFavoriteMessage}
                  timeout={{ enter: 500, exit: 500 }}
                >
                  <Button variant="contained" sx={{ cursor: 'default' }}>
                    Favorite added to My Portal
                  </Button>
                </Fade>
              </Box>
            </div>
            <Box className="filled-container">
              <Box className="home-info">
                <Box sx={{ width: '55%' }}>
                  <Typography sx={{ textAlign: 'start' }} variant="h4">
                    {homeModelData?.name}
                  </Typography>
                </Box>
                <Box className="extra-info">
                  <Typography variant="h5" className="weighted-typography">
                    {`${homeModelData?.bedrooms || 0} Bed | ${homeModelData?.bathrooms || 0} Bath | ${homeModelData?.square_feet || 0} sqft.`}
                  </Typography>

                  <Typography variant="h5" className="muted-typography">
                    {priceRangeText}
                  </Typography>
                </Box>
              </Box>

              <Button
                sx={{ width: '100%', margin: '10px 0px', marginTop: '20px' }}
                onClick={() => navigateToPanorama()}
              >
                <Typography>Customize My Home</Typography>
              </Button>
            </Box>
          </Box>
          <Box className="item">
            {homeModelData?.home_placement_model_2d_id && (
              <Box>
                <LazyLoadImage
                  alt="property"
                  effect="blur"
                  src={
                    cloudinaryBaseUrl +
                      homeModelData?.home_placement_model_2d_id || ''
                  }
                  width={'100%'}
                  height={320}
                  style={{ objectFit: 'cover' }}
                  useIntersectionObserver={true}
                />
              </Box>
            )}
            <Box className="filled-container description-container">
              <Typography variant="h5" className="weighted-typography">
                Description
              </Typography>
              <Typography>{homeModelData?.description || ''}</Typography>
            </Box>
          </Box>
        </Box>
        <ShareModal
          isOpen={showingShareModal}
          setIsOpen={setShowingShareModal}
          selectedPropertyId={`models/details?id=${id ? id.toString() : ''}`}
        />
      </Box>
    );
  }
};
